import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateCustomer, useMe, useRef } from '../api/hooks';
import { useFormDraft } from '../api/drafts';
import { AddressFields, type Address } from '../components/AddressFields';
import { ChatWidget } from '../components/ChatWidget';
import { RefSelect } from '../components/RefSelect';
import { clean, mergeStrings } from '../lib/form';

export function NewCustomerPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const create = useCreateCustomer();
  const me = useMe();
  const terms = useRef('terms');
  const pays = useRef('payment-methods');
  const taxes = useRef('tax-codes');
  const types = useRef('customer-types');

  const [f, setF] = useState({
    displayName: '', companyName: '', title: '', givenName: '', middleName: '', familyName: '', suffix: '',
    email: '', phone: '', mobile: '', fax: '', website: '', notes: '',
    termId: '', paymentMethodId: '', taxCodeId: '', customerTypeId: '',
  });
  const [taxable, setTaxable] = useState(false);
  const [billAddr, setBill] = useState<Address>({});
  const [shipAddr, setShip] = useState<Address>({});
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));

  // Server-side autosave draft (one live customer draft per user).
  const { discard, savedAt } = useFormDraft('customer', { f, taxable, billAddr, shipAddr }, (p) => {
    const d = p as { f?: Partial<typeof f>; taxable?: boolean; billAddr?: Address; shipAddr?: Address };
    if (d.f) setF((cur) => ({ ...cur, ...d.f }));
    if (typeof d.taxable === 'boolean') setTaxable(d.taxable);
    if (d.billAddr) setBill(d.billAddr);
    if (d.shipAddr) setShip(d.shipAddr);
  });
  const clearDraft = () => {
    void discard();
    qc.invalidateQueries({ queryKey: ['drafts'] });
  };

  const applyExtract = (x: Record<string, unknown>) => {
    setF((cur) => mergeStrings(cur, x));
    if (x.billAddr && typeof x.billAddr === 'object') setBill((a) => ({ ...a, ...(x.billAddr as Address) }));
    if (x.shipAddr && typeof x.shipAddr === 'object') setShip((a) => ({ ...a, ...(x.shipAddr as Address) }));
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate(clean({ ...f, taxable: taxable || undefined, billAddr, shipAddr }), {
      onSuccess: () => { clearDraft(); navigate('/customers'); },
    });
  };

  return (
    <>
      <h1>Add Customer</h1>
      <ChatWidget
        formType="customer"
        enabled={me.data?.assistantEnabled ?? false}
        onExtract={applyExtract}
        placeholder="e.g. “Acme Corp, ap@acme.com, 1 King St W, Hamilton ON” — or attach a business card / letterhead."
      />
      <form onSubmit={submit} style={{ maxWidth: 720 }}>
        {create.isSuccess && <div className="banner ok">Customer created in QuickBooks.</div>}
        {create.error && <div className="banner error">{(create.error as Error).message}</div>}

        <div className="card">
          <h2>Basics</h2>
          <div className="field">
            <label>Display name *</label>
            <input required value={f.displayName} onChange={(e) => set('displayName', e.target.value)} />
          </div>
          <div className="field">
            <label>Company name</label>
            <input value={f.companyName} onChange={(e) => set('companyName', e.target.value)} />
          </div>
          <div className="row cols-3">
            <div className="field"><label>Title</label><input value={f.title} onChange={(e) => set('title', e.target.value)} /></div>
            <div className="field"><label>First name</label><input value={f.givenName} onChange={(e) => set('givenName', e.target.value)} /></div>
            <div className="field"><label>Last name</label><input value={f.familyName} onChange={(e) => set('familyName', e.target.value)} /></div>
          </div>
        </div>

        <div className="card">
          <h2>Contact</h2>
          <div className="row cols-2">
            <div className="field"><label>Email</label><input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} /></div>
            <div className="field"><label>Website</label><input value={f.website} onChange={(e) => set('website', e.target.value)} /></div>
          </div>
          <div className="row cols-3">
            <div className="field"><label>Phone</label><input value={f.phone} onChange={(e) => set('phone', e.target.value)} /></div>
            <div className="field"><label>Mobile</label><input value={f.mobile} onChange={(e) => set('mobile', e.target.value)} /></div>
            <div className="field"><label>Fax</label><input value={f.fax} onChange={(e) => set('fax', e.target.value)} /></div>
          </div>
        </div>

        <div className="card">
          <h2>Addresses</h2>
          <AddressFields legend="Billing address" value={billAddr} onChange={setBill} />
          <AddressFields legend="Shipping address" value={shipAddr} onChange={setShip} />
        </div>

        <div className="card">
          <h2>Terms &amp; tax</h2>
          <div className="row cols-2">
            <RefSelect label="Terms" value={f.termId} onChange={(v) => set('termId', v)} options={terms.data} />
            <RefSelect label="Payment method" value={f.paymentMethodId} onChange={(v) => set('paymentMethodId', v)} options={pays.data} />
          </div>
          <div className="row cols-2">
            <RefSelect label="Default tax code" value={f.taxCodeId} onChange={(v) => set('taxCodeId', v)} options={taxes.data} />
            <RefSelect label="Customer type" value={f.customerTypeId} onChange={(v) => set('customerTypeId', v)} options={types.data} />
          </div>
          <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontWeight: 500 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={taxable} onChange={(e) => setTaxable(e.target.checked)} /> Taxable
          </label>
          <div className="field" style={{ marginTop: '1rem' }}>
            <label>Notes</label>
            <textarea rows={2} value={f.notes} onChange={(e) => set('notes', e.target.value)} />
          </div>
        </div>

        <div className="btn-row">
          <button className="primary" type="submit" disabled={create.isPending || !f.displayName.trim()}>
            {create.isPending ? 'Saving…' : 'Save customer'}
          </button>
          <button type="button" className="link" onClick={() => { clearDraft(); navigate('/customers'); }}>Discard draft</button>
          <span className="muted">Autosaves as a draft. Save creates the customer in QuickBooks.{savedAt ? ' · Draft saved' : ''}</span>
        </div>
      </form>
    </>
  );
}
